import { useState } from 'react';
import { useSeoMeta } from '@unhead/react';
import { EyeOff, MessageCircle, Send, User, Zap } from 'lucide-react';
import type { NostrEvent } from '@nostrify/nostrify';

import { LoginArea } from '@/components/auth/LoginArea';
import { WalletModal } from '@/components/WalletModal';
import { ZapButton } from '@/components/ZapButton';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { useAuthor } from '@/hooks/useAuthor';
import { useCommunityFeed, useCommunityPost } from '@/hooks/useCommunity';
import { useToast } from '@/hooks/useToast';
import { cn } from '@/lib/utils';
import {
  SAMPLE_THREADS,
  isAnonymousPost,
  timeAgo,
  type SampleThread,
  type Thread,
} from '@/lib/community';

/* ------------------------------------------------------------------ */
/* Shared bits                                                         */
/* ------------------------------------------------------------------ */

function shortKey(pubkey: string): string {
  return `${pubkey.slice(0, 6)}…${pubkey.slice(-4)}`;
}

function PostingToggle({
  anonymous,
  onChange,
  compact = false,
}: {
  anonymous: boolean;
  onChange: (anonymous: boolean) => void;
  compact?: boolean;
}) {
  return (
    <div className="grid grid-cols-2 gap-2" role="group" aria-label="Posting identity">
      <button
        type="button"
        onClick={() => onChange(false)}
        aria-pressed={!anonymous}
        className={cn(
          'flex items-center justify-center gap-2 rounded-xl border-2 font-semibold transition-colors',
          compact ? 'h-10 text-sm' : 'h-12 text-base',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          !anonymous
            ? 'border-aqua-strong/50 bg-aqua text-aqua-foreground'
            : 'border-border bg-white text-muted-foreground hover:bg-aqua/40',
        )}
      >
        <User className="size-4" aria-hidden />
        Publicly
      </button>
      <button
        type="button"
        onClick={() => onChange(true)}
        aria-pressed={anonymous}
        className={cn(
          'flex items-center justify-center gap-2 rounded-xl border-2 font-semibold transition-colors',
          compact ? 'h-10 text-sm' : 'h-12 text-base',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          anonymous
            ? 'border-lavender-strong/50 bg-lavender text-lavender-foreground'
            : 'border-border bg-white text-muted-foreground hover:bg-lavender/40',
        )}
      >
        <EyeOff className="size-4" aria-hidden />
        Anonymously
      </button>
    </div>
  );
}

/** Author avatar + name for a real Nostr event (handles anonymous marker). */
function EventAuthor({ event, small = false }: { event: NostrEvent; small?: boolean }) {
  const author = useAuthor(event.pubkey);
  const metadata = author.data?.metadata;
  const anonymous = isAnonymousPost(event);

  const name = anonymous
    ? 'Anonymous'
    : metadata?.display_name || metadata?.name || shortKey(event.pubkey);
  const picture =
    !anonymous && metadata?.picture?.startsWith('https://') ? metadata.picture : undefined;

  return (
    <div className="flex items-center gap-2.5">
      <Avatar className={small ? 'size-8' : 'size-11'}>
        {picture && <AvatarImage src={picture} alt="" />}
        <AvatarFallback
          className={cn(
            'font-bold',
            anonymous ? 'bg-lavender text-lavender-foreground' : 'bg-aqua text-aqua-foreground',
          )}
        >
          {anonymous ? (
            <EyeOff className={small ? 'size-3.5' : 'size-4'} aria-label="Anonymous" />
          ) : (
            name.replace('…', '').slice(0, 2).toUpperCase()
          )}
        </AvatarFallback>
      </Avatar>
      <div className="flex flex-wrap items-baseline gap-x-2">
        <span className={cn('font-bold', small ? 'text-sm' : 'text-base')}>{name}</span>
        <span className="text-xs text-muted-foreground">{timeAgo(event.created_at)}</span>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Ask box                                                             */
/* ------------------------------------------------------------------ */

function AskBox() {
  const [text, setText] = useState('');
  const [anonymous, setAnonymous] = useState(true);
  const { post, isPending, canPostPublicly } = useCommunityPost();
  const { toast } = useToast();

  const handlePost = async () => {
    const content = text.trim();
    if (!content) return;
    try {
      await post({ content, anonymous });
      setText('');
      toast({
        title: anonymous ? 'Posted anonymously' : 'Question posted',
        description: 'Your question is live at the top of the feed.',
      });
    } catch {
      // Error toast already shown by the hook.
    }
  };

  return (
    <Card className="rounded-3xl shadow-sm">
      <CardHeader>
        <CardTitle className="text-xl font-bold">Ask the community</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="What's on your mind? Ask about symptoms, flare-up routines, doctor visits…"
          aria-label="Ask a question"
          className="min-h-28 rounded-2xl text-base leading-relaxed"
        />
        <PostingToggle anonymous={anonymous} onChange={setAnonymous} />
        {!anonymous && !canPostPublicly && (
          <div className="flex flex-col items-center gap-3 rounded-2xl border border-dashed border-lavender-strong/40 bg-lavender/40 px-4 py-4 text-center">
            <p className="text-sm font-medium text-lavender-foreground">
              Log in to post publicly — or flip the toggle to post anonymously, no account needed.
            </p>
            <LoginArea />
          </div>
        )}
        <Button
          onClick={handlePost}
          disabled={!text.trim() || isPending || (!anonymous && !canPostPublicly)}
          className="h-14 w-full rounded-2xl text-lg font-bold shadow-sm"
        >
          <Send className="size-5" aria-hidden />
          {isPending ? 'Posting…' : anonymous ? 'Post anonymously' : 'Post publicly'}
        </Button>
      </CardContent>
    </Card>
  );
}

/* ------------------------------------------------------------------ */
/* Threads (real Nostr events)                                         */
/* ------------------------------------------------------------------ */

function ReplyComposer({ parentId }: { parentId: string }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [anonymous, setAnonymous] = useState(true);
  const { post, isPending, canPostPublicly } = useCommunityPost();
  const { toast } = useToast();

  const handleReply = async () => {
    const content = text.trim();
    if (!content) return;
    try {
      await post({ content, anonymous, parentId });
      setText('');
      setOpen(false);
      toast({ title: 'Reply posted' });
    } catch {
      // handled in hook
    }
  };

  if (!open) {
    return (
      <Button
        variant="ghost"
        onClick={() => setOpen(true)}
        className="h-11 rounded-xl px-4 font-semibold text-primary hover:bg-blush/60"
      >
        <MessageCircle className="size-5" aria-hidden />
        Reply
      </Button>
    );
  }

  return (
    <div className="space-y-3">
      <Textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Write a supportive reply…"
        aria-label="Write a reply"
        autoFocus
        className="min-h-20 rounded-2xl text-base"
      />
      <PostingToggle anonymous={anonymous} onChange={setAnonymous} compact />
      <div className="flex gap-2">
        <Button
          onClick={handleReply}
          disabled={!text.trim() || isPending || (!anonymous && !canPostPublicly)}
          className="h-11 flex-1 rounded-xl font-bold"
        >
          <Send className="size-4" aria-hidden />
          {isPending ? 'Posting…' : 'Reply'}
        </Button>
        <Button
          variant="ghost"
          onClick={() => setOpen(false)}
          className="h-11 rounded-xl font-semibold"
        >
          Cancel
        </Button>
      </div>
    </div>
  );
}

function ThreadCard({ thread }: { thread: Thread }) {
  const { root, replies } = thread;

  return (
    <Card className="rounded-3xl shadow-sm">
      <CardContent className="space-y-4 p-5">
        <div className="space-y-2">
          <EventAuthor event={root} />
          <p className="whitespace-pre-wrap text-[15px] leading-relaxed">{root.content}</p>
        </div>

        {replies.length > 0 && (
          <div className="space-y-3 border-l-2 border-lavender pl-4">
            {replies.map((reply) => (
              <div key={reply.id} className="space-y-1.5">
                <div className="flex items-start justify-between gap-2">
                  <EventAuthor event={reply} small />
                  <ZapButton
                    target={reply}
                    className="h-9 shrink-0 rounded-xl bg-amber-100/80 px-3 text-xs font-bold text-amber-700 transition-colors hover:bg-amber-200"
                  />
                </div>
                <p className="whitespace-pre-wrap text-sm leading-relaxed text-foreground/90">
                  {reply.content}
                </p>
              </div>
            ))}
          </div>
        )}

        <div className="flex items-center gap-2">
          <ReplyComposer parentId={root.id} />
          <ZapButton
            target={root}
            className="h-11 rounded-xl bg-amber-100 px-4 text-sm font-bold text-amber-700 transition-colors hover:bg-amber-200"
          />
          {replies.length > 0 && (
            <span className="text-sm font-medium text-muted-foreground">
              {replies.length} {replies.length === 1 ? 'reply' : 'replies'}
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

/* ------------------------------------------------------------------ */
/* Sample threads (offline fallback only)                              */
/* ------------------------------------------------------------------ */

function SampleAvatar({ name }: { name: string }) {
  const anonymous = name === 'Anonymous';
  return (
    <Avatar className="size-11">
      <AvatarFallback
        className={cn(
          'font-bold',
          anonymous ? 'bg-lavender text-lavender-foreground' : 'bg-aqua text-aqua-foreground',
        )}
      >
        {anonymous ? <EyeOff className="size-4" aria-label="Anonymous" /> : name.slice(0, 1)}
      </AvatarFallback>
    </Avatar>
  );
}

function SampleThreadCard({ thread }: { thread: SampleThread }) {
  const now = Date.now();
  return (
    <Card className="rounded-3xl shadow-sm">
      <CardContent className="space-y-4 p-5">
        <div className="space-y-2">
          <div className="flex items-center gap-2.5">
            <SampleAvatar name={thread.root.authorName} />
            <div className="flex flex-wrap items-baseline gap-x-2">
              <span className="font-bold">{thread.root.authorName}</span>
              <span className="text-xs text-muted-foreground">
                {timeAgo((now - thread.root.postedHoursAgo * 3_600_000) / 1000)}
              </span>
            </div>
          </div>
          <p className="whitespace-pre-wrap text-[15px] leading-relaxed">{thread.root.content}</p>
        </div>

        {thread.replies.length > 0 && (
          <div className="space-y-3 border-l-2 border-lavender pl-4">
            {thread.replies.map((reply) => (
              <div key={reply.id} className="space-y-1.5">
                <div className="flex flex-wrap items-baseline gap-x-2">
                  <span className="text-sm font-bold">{reply.authorName}</span>
                  <span className="text-xs text-muted-foreground">
                    {timeAgo((now - reply.postedHoursAgo * 3_600_000) / 1000)}
                  </span>
                </div>
                <p className="whitespace-pre-wrap text-sm leading-relaxed text-foreground/90">
                  {reply.content}
                </p>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */

function FeedSkeleton() {
  return (
    <Card className="rounded-3xl shadow-sm">
      <CardContent className="space-y-4 p-5">
        <div className="flex items-center gap-3">
          <Skeleton className="size-11 rounded-full" />
          <div className="space-y-2">
            <Skeleton className="h-4 w-28" />
            <Skeleton className="h-3 w-16" />
          </div>
        </div>
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-4/5" />
        <Skeleton className="h-4 w-3/5" />
      </CardContent>
    </Card>
  );
}

export default function CommunityPage() {
  useSeoMeta({
    title: 'Better Me — Community Support',
    description:
      'A gentle space to ask questions and share experiences. Post publicly or anonymously.',
  });

  const { threads, isLoading } = useCommunityFeed();
  const showSamples = !isLoading && threads.length === 0;

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-3">
        <div className="space-y-1">
          <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">Community support</h1>
          <p className="text-base text-muted-foreground">
            A gentle space for questions and shared experience — public or anonymous.
          </p>
        </div>
        <WalletModal>
          <Button
            variant="outline"
            className="h-12 shrink-0 rounded-2xl border-amber-300/60 bg-amber-50 px-4 font-semibold text-amber-700 hover:bg-amber-100"
          >
            <Zap className="size-5" aria-hidden />
            <span className="hidden sm:inline">Wallet</span>
          </Button>
        </WalletModal>
      </div>

      <AskBox />

      {isLoading ? (
        <div className="space-y-4">
          <FeedSkeleton />
          <FeedSkeleton />
          <FeedSkeleton />
        </div>
      ) : showSamples ? (
        <div className="space-y-4">
          <p className="text-center text-xs text-muted-foreground">
            Showing sample conversations while relays connect…
          </p>
          {SAMPLE_THREADS.map((thread) => (
            <SampleThreadCard key={thread.root.id} thread={thread} />
          ))}
        </div>
      ) : (
        <div className="space-y-4">
          {threads.map((thread) => (
            <ThreadCard key={thread.root.id} thread={thread} />
          ))}
        </div>
      )}
    </div>
  );
}
